<script setup>
import { storeToRefs } from 'pinia'
import SocialWebsite from 'assets/icons/social-website-outline.svg'
import SocialPhone from 'assets/icons/social-phone-outline.svg'
import SocialEmail from 'assets/icons/social-email-outline.svg'
import SocialInstagram from 'assets/icons/social-instagram-outline.svg'
import SocialFacebook from 'assets/icons/social-facebook-outline.svg'
import { useMessageStore } from '~/stores/messages'

// Contact props
const props = defineProps({
    centerContacts: {
        type: Object,
        default: () => ({}),
    }
})

// Fetch route
const route = useRoute()

// Fetch Center Details
const centerRoute = computed(() => route.params?.centerUid)
const { data: centerDetails, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)

// Prefetch data on the server side
onServerPrefetch(async () => {
    await centerDetailSuspense() // Wait for the query to finish fetching
})

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Get email link
const getEmailLink = computed(() => getFormattedEmailLink(props.centerContacts?.email))

// Get phone link
const getPhoneLink = computed(() => getFormattedPhoneLink(props.centerContacts?.phone))

// Get website link
const getWebsiteLink = computed(() => getFormattedWebsiteLink(props.centerContacts?.website))

// Get instagram link
const getInstagramLink = computed(() => getFormattedWebsiteLink(props.centerContacts?.instagram, staticKeys.value?.center_socialLinks_instagram))

// Get facebook link
const getFacebookLink = computed(() => getFormattedWebsiteLink(props.centerContacts?.facebook, staticKeys.value?.center_socialLinks_facebook))

// Track user click
const trackClicks = (type) => {
    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.CENTER_CONTACT_CLICK,
        centerUid: centerDetails.value?.centerUid,
        centerName: centerDetails.value?.centerName,
        contactType: type,
        screenName: TRACKING_CONSTANTS.MISC.CENTER_DETAILS,
    })
}

const centerContactsItems = computed(() => {
    return [
        {
            render: getPhoneLink.value,
            icon: SocialPhone,
            fn: () => trackClicks('phone')
        },
        {
            render: getEmailLink.value,
            icon: SocialEmail,
            fn: () => trackClicks('email')
        },
        {
            render: getWebsiteLink.value,
            icon: SocialWebsite,
            fn: () => trackClicks('website')
        },
        {
            render: getInstagramLink.value,
            icon: SocialInstagram,
            fn: () => trackClicks('instagram')
        },
        {
            render: getFacebookLink.value,
            icon: SocialFacebook,
            fn: () => trackClicks('facebook')
        }
    ]
})
</script>

<template>
    <table class="contact-table">
        <tbody>
            <template v-for="(item, index) in centerContactsItems" :key="`center-contact-${index}`">
                <tr v-if="item.render">
                    <td><component :is="item.icon" /></td>
                    <td>
                        <a
                            :href="item.render.link"
                            target="_blank"
                            :title="item.render.label"
                            rel="noopener noreferrer"
                            @click="item.fn"
                        >
                            {{ item.render.label }}
                        </a>
                    </td>
                </tr>
            </template>
        </tbody>
    </table>
</template>

<style lang="scss">
.contact-table {
    width: 100%;

    @include body2-medium;

    td {
        padding: 7px 0;
        vertical-align: middle;

        &:first-child {
            padding: 7px 20px 7px 0;
            white-space: nowrap;
            width: 1px;
            vertical-align: middle;
        }

        &:last-child {
            width: 100%;
        }
    }

    a {
        text-decoration: none;
        color: inherit;
        display: block;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        max-width: calc(100vw - 172px);

        @include bp(500px) {
            max-width: 224px;
        }

        @include bp(medium1) {
            max-width: 224px;
        }

        &:hover {
            text-decoration: underline;
        }
    }

    svg {
        width: 24px;
        max-height: 24px;
        display: block;
    }
}
</style>
